import React, { useState, useRef, useEffect } from "react";
import { FaSort, FaChevronDown } from "react-icons/fa";

const DeliverySortFilter = ({ deliveries, onSortChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentSort, setCurrentSort] = useState({ field: "date", direction: "desc" });
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  const sortOptions = [
    { label: "Date (Newest First)", field: "date", direction: "desc" },
    { label: "Date (Oldest First)", field: "date", direction: "asc" },
    { label: "Payment Status", field: "payment_status", direction: "asc" },
    { label: "Total Price (High to Low)", field: "total_price", direction: "desc" },
    { label: "Total Price (Low to High)", field: "total_price", direction: "asc" },
    { label: "Paid Amount (High to Low)", field: "paid_amount", direction: "desc" },
    { label: "Paid Amount (Low to High)", field: "paid_amount", direction: "asc" },
    { label: "Remaining Amount (High to Low)", field: "remaining_payment", direction: "desc" },
    { label: "Remaining Amount (Low to High)", field: "remaining_payment", direction: "asc" },
  ];

  const handleSort = (sortOption) => {
    setCurrentSort(sortOption);
    setIsOpen(false);

    // Create a sorted copy of deliveries based on the selected sort option
    const sortedDeliveries = [...deliveries].sort((a, b) => {
      // Special handling for dates
      if (sortOption.field === "date") {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return sortOption.direction === "asc" 
          ? dateA - dateB 
          : dateB - dateA;
      }
      
      // Special handling for payment status (custom order)
      if (sortOption.field === "payment_status") {
        const statusOrder = {
          "pre-paid": 1,
          "Pre-Paid": 1,
          "Part Payment": 2,
          "post-paid": 3,
          "Unpaid": 4
        };
        
        const statusA = statusOrder[a.payment_status] || 5;
        const statusB = statusOrder[b.payment_status] || 5;
        
        return statusA - statusB;
      }
      
      // Default handling for numeric fields
      const valueA = a[sortOption.field];
      const valueB = b[sortOption.field];
      
      if (sortOption.direction === "asc") {
        return valueA - valueB;
      } else {
        return valueB - valueA;
      }
    });

    // Call the parent component's callback with the sorted results
    onSortChange(sortedDeliveries);
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!isOpen) return;
      const clickedInsideDropdown = dropdownRef.current?.contains(event.target);
      const clickedOnButton = buttonRef.current?.contains(event.target);
      if (!clickedInsideDropdown && !clickedOnButton) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  return (
    <div className="relative inline-block">
      <button
        className="bg-gray-100 text-gray-700 font-medium px-4 py-2 rounded-md flex items-center space-x-2 hover:bg-gray-200"
        onClick={() => setIsOpen(!isOpen)}
        ref={buttonRef}
      >
        <FaSort className="text-gray-600" />
        <span>Sort</span>
        <FaChevronDown className={`text-gray-600 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white rounded-md shadow-lg z-10 py-1 border border-gray-200" ref={dropdownRef}>
          {sortOptions.map((option, index) => (
            <button
              key={index}
              className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                currentSort.field === option.field && currentSort.direction === option.direction
                  ? "bg-purple-50 text-purple-700 font-medium"
                  : "text-gray-700"
              }`}
              onClick={() => handleSort(option)}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default DeliverySortFilter;